import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Register from './pages/Register';
import PasswordReset from './pages/PasswordReset';
import PasswordResetSuccess from './pages/PasswordResetSuccess';
import PasswordResetConfirm from './pages/PasswordResetConfirm';
import PasswordResetComplete from './pages/PasswordResetComplete';
import EmailVerification from './pages/EmailVerification';
import GoogleCallback from './pages/GoogleCallback';
import OrganizationCreation from './pages/OrganizationCreation';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import Reservations from './pages/Reservations';
import Staff from './pages/Staff';
// import ReservationsImproved from './pages/ReservationsImproved';
// import ReservationsDebug from './pages/ReservationsDebug';
import Sales from './pages/Sales';
import AIAnalysis from './pages/AIAnalysis';
import Settings from './pages/Settings';
import Help from './pages/Help';
import CustomerDetail from './pages/CustomerDetail';
import TreatmentNew from './pages/TreatmentNew';
import TreatmentDetail from './pages/TreatmentDetail';
import JoinOrganization from './pages/JoinOrganization';
import OrganizationSettings from './pages/OrganizationSettings';
import DashboardLayout from './components/layout/DashboardLayout';
import { useAuthContext } from './hooks/useAuth';

// 認証状態チェック用コンポーネント（統一版）
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthContext();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// 権限チェック付きルート保護コンポーネント
const RoleProtectedRoute: React.FC<{ 
  children: React.ReactNode; 
  requiredRole?: 'owner' | 'staff';
  requiredPermission?: string;
}> = ({ children, requiredRole, requiredPermission }) => {
  const { isAuthenticated, user, checkPermission } = useAuthContext();
  
  // デバッグログ追加
  console.log('🔍 [RoleProtectedRoute] 権限チェック開始');
  console.log('  - isAuthenticated:', isAuthenticated);
  console.log('  - user:', user);
  console.log('  - user.role:', user?.role);
  console.log('  - requiredRole:', requiredRole);
  console.log('  - requiredPermission:', requiredPermission);
  console.log('  - 現在のパス:', window.location.pathname);
  
  // まず認証チェック
  if (!isAuthenticated) {
    console.log('❌ [RoleProtectedRoute] 未認証のためログインへリダイレクト');
    return <Navigate to="/login" replace />;
  }
  
  // Role制御: 特定の役割が必要な場合
  if (requiredRole === 'owner' && user?.role !== 'owner') {
    console.log('❌ [RoleProtectedRoute] owner権限不足のためダッシュボードへリダイレクト');
    console.log('  - 必要権限: owner');
    console.log('  - 現在の権限:', user?.role);
    return <Navigate to="/dashboard" replace />;
  }
  
  // Permission制御: 特定の権限が必要な場合
  if (requiredPermission && !checkPermission(requiredPermission)) {
    console.log('❌ [RoleProtectedRoute] 必要な権限が不足のためダッシュボードへリダイレクト');
    console.log('  - 必要権限:', requiredPermission);
    return <Navigate to="/dashboard" replace />;
  }
  
  console.log('✅ [RoleProtectedRoute] 権限チェック通過');
  return <>{children}</>;
};

// 404ハンドリング用コンポーネント（認証状態を考慮）
const NotFoundRedirect: React.FC = () => {
  const { isAuthenticated } = useAuthContext();
  return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
};
// ログイン済みユーザーのリダイレクト用コンポーネント（統一版）  
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthContext();
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

const AppRouter: React.FC = () => {
  return (
    <Routes>
      {/* パブリックルート - 未認証時のみアクセス可能 */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      <Route 
        path="/password-reset" 
        element={
          <PublicRoute>
            <PasswordReset />
          </PublicRoute>
        } 
      />
      <Route
        path="/password-reset/success"
        element={
          <PublicRoute>
            <PasswordResetSuccess />
          </PublicRoute>
        }
      />
      <Route
        path="/password-reset/confirm/:token"
        element={
          <PublicRoute>
            <PasswordResetConfirm />
          </PublicRoute>
        }
      />
      <Route
        path="/password-reset/complete"
        element={
          <PublicRoute>
            <PasswordResetComplete />
          </PublicRoute>
        }
      />
      <Route
        path="/auth/google/callback"
        element={<GoogleCallback />}
      />
      <Route
        path="/email-verification/:userId"
        element={
          <PublicRoute>
            <EmailVerification />
          </PublicRoute>
        }
      />

      {/* Phase 4: 組織作成ルート - 認証必須、新規ユーザー向け */}
      <Route
        path="/organization-creation"
        element={
          <ProtectedRoute>
            <OrganizationCreation />
          </ProtectedRoute>
        }
      />

      {/* 🆕 Issue #268: 招待承認ルート - 認証不要（公開リンク） */}
      <Route
        path="/join/:invitationToken"
        element={<JoinOrganization />}
      />

      {/* 保護されたルート - 認証必須（DashboardLayout内にネスト） */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/customers/:id" element={<CustomerDetail />} />
        <Route path="/customers/:id/treatment/new" element={<TreatmentNew />} />
        <Route path="/customers/:id/treatment/:tid" element={<TreatmentDetail />} />
        <Route path="/staff" element={<Staff />} />
        <Route path="/reservations" element={<Reservations />} />
        <Route
          path="/sales"
          element={
            <RoleProtectedRoute requiredRole="owner">
              <Sales />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/ai-analysis"
          element={
            <RoleProtectedRoute requiredRole="owner">
              <AIAnalysis />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/organization/settings"
          element={
            <RoleProtectedRoute requiredRole="owner">
              <OrganizationSettings />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <RoleProtectedRoute requiredRole="owner">
              <Settings />
            </RoleProtectedRoute>
          }
        />
        <Route path="/help" element={<Help />} />
      </Route>
      
      {/* 404 - 未認証の場合はログインへ、認証済みの場合はダッシュボードへ */}
      <Route 
        path="*" 
        element={<NotFoundRedirect />} 
      />
    </Routes>
  );
};

export default AppRouter;